/**
 * Gestión → Administración → Generador masivo de boletas
 * (`/gestion/administracion/generador-masivo`).
 *
 * Puerto de `Reportes::boleta_generator_controller` + `Reportes::facturar_list`
 * + `Reportes::get_boleta_generar_list`.
 *
 * Qué se porta:
 * - El **formulario** de emisión (producto, precio, monto/volumen y boletas a
 *   generar) con el cálculo de **cantidad (GLN/LTR) por boleta** y **total por
 *   boleta** (`calcular_boletas_calcular`).
 * - La **previsualización** que lista las boletas que se emitirían sin escribir
 *   nada en la base.
 * - La **emisión** de N boletas (cada una con su venta, su boleta y su línea),
 *   con el límite de S/ 700.00 por boleta.
 */

import { Boxes, Cog, Eye, FileCheck2, Ticket } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { nuevaClaveDeEnvio } from '@/lib/clave-de-envio';

import { AdministracionNav } from '../components/administracion-nav';
import {
  useCatalogosBoletas,
  useGeneradorMasivo,
  usePrevisualizarGenerador,
} from '../administracion-boletas.api';
import { fechaCorta, hoyIso, hoyMenosDias, monto } from '../gestion-formato';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Redondea como el `round()` del bundle original. */
function redondear(valor: number, decimales: number): number {
  const factor = 10 ** decimales;

  return Math.round((valor + Number.EPSILON) * factor) / factor;
}

export function GeneradorMasivoPage() {
  useDocumentTitle('Gestión · Generador masivo de boletas');

  const catalogos = useCatalogosBoletas();
  const previsualizar = usePrevisualizarGenerador();
  const generador = useGeneradorMasivo();

  // Clave del lote: si el envío se repite (timeout de red), el API no vuelve a emitir las
  // boletas. Se renueva al confirmar.
  const claveDelLote = useRef(nuevaClaveDeEnvio());

  const [productoId, setProductoId] = useState('');
  const [precio, setPrecio] = useState('');
  const [unidad, setUnidad] = useState('');
  const [serie, setSerie] = useState('');
  const [fecha, setFecha] = useState(() => hoyIso());
  const [montoTotal, setMontoTotal] = useState('');
  const [volumenTotal, setVolumenTotal] = useState('');
  const [boletas, setBoletas] = useState('');

  const [confirmar, setConfirmar] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const empresa = catalogos.data?.empresa;
  const productos = catalogos.data?.productos ?? [];
  const series = catalogos.data?.series ?? [];

  const serieEfectiva = serie || series[0] || '';
  const diasMaximos = empresa?.dias_boleta_masiva ?? 0;
  const precioMaximo = empresa?.precio_producto_boleta_masiva ?? 0;

  const precioNumero = Number.parseFloat(precio) || 0;
  const boletasNumero = Number.parseInt(boletas, 10) || 0;
  const montoNumero = Number.parseFloat(montoTotal) || 0;

  // Cantidad (GLN/LTR) por boleta y su total (calcular_boletas_calcular).
  const totalPorBoleta = useMemo(() => {
    if (precioNumero > 0 && boletasNumero > 0 && montoNumero > 0) {
      const cantidad = redondear(montoNumero / boletasNumero / precioNumero, 3);

      return cantidad > 0 ? cantidad : 0;
    }

    return 0;
  }, [precioNumero, boletasNumero, montoNumero]);

  const totalPorBoletaMonto = totalPorBoleta > 0 ? redondear(totalPorBoleta * precioNumero, 2) : 0;

  const elegirProducto = (valor: string) => {
    setProductoId(valor);
    const producto = productos.find((p) => String(p.id) === valor);

    if (producto) {
      setPrecio(String(producto.precio));
      setUnidad(producto.unidad_medida ?? '');
    }
  };

  const alCambiarMonto = (valor: string) => {
    setMontoTotal(valor);
    const cifra = Number.parseFloat(valor) || 0;

    if (precioNumero > 0 && cifra > 0) setVolumenTotal(String(redondear(cifra / precioNumero, 3)));
    else setVolumenTotal('');
  };

  const alCambiarVolumen = (valor: string) => {
    setVolumenTotal(valor);
    const cifra = Number.parseFloat(valor) || 0;

    if (precioNumero > 0 && cifra > 0) setMontoTotal(String(redondear(cifra * precioNumero, 2)));
    else setMontoTotal('');
  };

  const peticion = {
    producto_id: Number.parseInt(productoId, 10),
    precio_producto: precioNumero,
    cantidad_producto: boletasNumero,
    monto: montoNumero,
    total: totalPorBoleta,
    fecha_generar: fecha,
    empresa_serie: serieEfectiva,
    numero_cliente: '00000000',
    nombre_cliente: 'CLIENTES VARIOS',
    unidad_medida: unidad,
  };

  const listoParaPrevisualizar =
    productoId !== '' &&
    precioNumero > 0 &&
    boletasNumero > 0 &&
    montoNumero > 0 &&
    totalPorBoleta > 0 &&
    totalPorBoletaMonto > 0 &&
    totalPorBoletaMonto <= 700 &&
    serieEfectiva !== '' &&
    fecha !== '' &&
    (precioMaximo <= 0 || precioNumero <= precioMaximo);

  const previsualizacion = previsualizar.data?.boletas ?? [];
  const sumaPrevisualizacion = previsualizacion.reduce((suma, fila) => suma + fila.total, 0);

  const previsualizarAhora = () => {
    setError(null);
    previsualizar.mutate(peticion, {
      onError: (fallo) => {
        setError(fallo instanceof ApiError ? fallo.message : 'No se pudo previsualizar.');
      },
    });
  };

  const ejecutar = () => {
    setConfirmar(false);
    setError(null);

    generador.mutate({ ...peticion, clave_idempotencia: claveDelLote.current }, {
      onSuccess: (resultado) => {
        claveDelLote.current = nuevaClaveDeEnvio();
        setAviso(resultado.mensaje ?? `Se generaron ${resultado.cantidad_emitido} boletas.`);
        setPrevisualizarNulo();
      },
      onError: (fallo) => {
        setError(fallo instanceof ApiError ? fallo.message : 'No se pudo generar las boletas.');
      },
    });
  };

  const setPrevisualizarNulo = () => {
    previsualizar.reset();
  };

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold text-ink-900">Generador masivo de boletas</h1>
        <p className="mt-1 text-sm text-ink-500">
          Emite boletas de un combustible con validaciones automáticas (límite de S/ 700.00 por
          boleta).
        </p>
      </header>

      <AdministracionNav active="generador" />

      {catalogos.data && series.length === 0 ? (
        <Alert variant="info" title="No hay series masivas de boleta">
          Configura una serie con <code>operationType = "masivo"</code> y prefijo <code>B</code> en
          Documentos Fiscales → Series para poder emitir.
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="error" title="No se pudo completar la acción">
          {error}
        </Alert>
      ) : null}

      {/* Datos generales */}
      <div className="rounded-card border border-ink-100 p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-800">
          <FileCheck2 className="size-4 text-brand-500" aria-hidden="true" />
          Datos generales
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Input label="RUC" value={empresa?.ruc ?? ''} readOnly />
          <Input label="Empresa" value={empresa?.nom_comercial ?? ''} readOnly />
          <Input label="Tipo comprobante" value="BOLETA" readOnly />
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Serie</span>
            <Select
              className={CONTROL}
              value={serieEfectiva}
              disabled={series.length === 0}
              onChange={(evento) => setSerie(evento.target.value)}
            >
              {series.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </label>
          <Input
            label="Fecha"
            type="date"
            value={fecha}
            min={hoyMenosDias(diasMaximos)}
            max={hoyIso()}
            onChange={(evento) => setFecha(evento.target.value)}
          />
          <Input label="Método de pago" value="CONTADO" readOnly />
          <Input label="Nombre cliente" value="CLIENTES VARIOS" readOnly />
          <Input label="Número cliente" value="00000000" readOnly />
        </div>
      </div>

      {/* Producto */}
      <div className="rounded-card border border-ink-100 p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-800">
          <Boxes className="size-4 text-brand-500" aria-hidden="true" />
          Datos del producto
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Producto</span>
            <Select
              className={CONTROL}
              value={productoId}
              onChange={(evento) => elegirProducto(evento.target.value)}
            >
              <option value="">Seleccione</option>
              {productos.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.nombre}
                </option>
              ))}
            </Select>
          </label>
          <Input
            label="Precio producto"
            type="number"
            min="0"
            step="0.01"
            value={precio}
            onChange={(evento) => setPrecio(evento.target.value)}
          />
          <Input label="Unidad de medida" value={unidad} readOnly />
        </div>

        {precioMaximo > 0 && precioNumero > precioMaximo ? (
          <p className="mt-2 text-xs font-medium text-rose-600">
            El precio no puede ser mayor a S/ {precioMaximo.toFixed(2)} (precio máximo de
            administración).
          </p>
        ) : null}
      </div>

      {/* Cantidades */}
      <div className="rounded-card border border-ink-100 p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-800">
          <Cog className="size-4 text-brand-500" aria-hidden="true" />
          Datos de cantidades
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Input
            label="Monto total (S/.)"
            type="number"
            min="0"
            step="0.01"
            value={montoTotal}
            onChange={(evento) => alCambiarMonto(evento.target.value)}
          />
          <Input
            label="Volumen total (GLL)"
            type="number"
            min="0"
            step="0.01"
            value={volumenTotal}
            onChange={(evento) => alCambiarVolumen(evento.target.value)}
          />
          <Input
            label="Boletas x generar"
            type="number"
            min="0"
            step="1"
            value={boletas}
            onChange={(evento) => setBoletas(evento.target.value)}
          />
          <Input
            label="Cantidad (GLN/LTR) x boleta"
            value={totalPorBoleta ? totalPorBoleta.toFixed(3) : '0.000'}
            readOnly
          />
          <Input
            label="Total (S/.) x boleta"
            value={totalPorBoletaMonto ? totalPorBoletaMonto.toFixed(2) : '0.00'}
            readOnly
          />
        </div>

        {totalPorBoletaMonto > 700 ? (
          <p className="mt-2 text-xs font-medium text-rose-600">
            El total por boleta no puede superar los S/ 700.00. Ajusta el monto, el precio o el
            número de boletas.
          </p>
        ) : null}

        <div className="mt-4 flex flex-wrap gap-3">
          <Button
            leftIcon={<Eye className="size-4" aria-hidden="true" />}
            disabled={!listoParaPrevisualizar || previsualizar.isPending}
            onClick={previsualizarAhora}
          >
            Previsualizar
          </Button>
          <Button
            variant="danger"
            leftIcon={<Ticket className="size-4" aria-hidden="true" />}
            disabled={!listoParaPrevisualizar || previsualizacion.length === 0 || generador.isPending}
            onClick={() => setConfirmar(true)}
          >
            Generar las boletas
          </Button>
          {(previsualizar.isPending || generador.isPending) ? (
            <Badge variant="warning">Procesando…</Badge>
          ) : null}
        </div>
      </div>

      {/* Previsualización */}
      {previsualizacion.length > 0 ? (
        <div className="rounded-card border border-ink-100 p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-800">
            <Eye className="size-4 text-brand-500" aria-hidden="true" />
            Previsualización ({previsualizacion.length} boletas)
          </div>

          <div className="overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[13px]">
              <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2 whitespace-nowrap">Fecha</th>
                  <th className="px-3 py-2 whitespace-nowrap">Cliente</th>
                  <th className="px-3 py-2 whitespace-nowrap">Serie</th>
                  <th className="px-3 py-2 whitespace-nowrap">Número</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Precio</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Cantidad</th>
                  <th className="px-3 py-2 whitespace-nowrap">Unidad</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">Monto (S/.)</th>
                </tr>
              </thead>
              <tbody>
                {previsualizacion.map((fila) => (
                  <tr key={fila.orden} className="border-t border-ink-100">
                    <td className="px-3 py-2 whitespace-nowrap">{fila.orden}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{fechaCorta(fila.fecha_generar)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{fila.cliente}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{fila.serie}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{fila.numero}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{monto(fila.precio)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{fila.cantidad.toFixed(3)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{fila.unidad_medida || '—'}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{monto(fila.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-ink-100 bg-ink-50/50">
                  <td className="px-3 py-2 font-semibold" colSpan={8}>
                    Suma
                  </td>
                  <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                    {monto(sumaPrevisualizacion)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmar}
        title="Generar las boletas"
        description={`Se generarán ${boletasNumero} boletas de S/ ${totalPorBoletaMonto.toFixed(2)} cada una. Esta acción no se puede deshacer.`}
        confirmLabel="Sí, generar"
        tone="danger"
        loading={generador.isPending}
        onConfirm={ejecutar}
        onClose={() => setConfirmar(false)}
      />
    </div>
  );
}
